import { FC } from 'react';

import classNames from 'classnames';

import ArchesToTreatInput from 'components/ArchesToTreatInput/ArchesToTreatInput';
import { MidlinesInput } from 'components/MidlinesInput/MidlinesInput';
import OcclusionCorrectionInput from 'components/OcclusionCorrectionInput/OcclusionCorrectionInput';
import { OverbiteInput } from 'components/OverbiteInput/OverbiteInput';
import { ProsthesisRestorationInput } from 'components/ProsthesisRestorationInput/ProsthesisRestorationInput';
import { RHFTextArea } from 'components/RHFTextArea/RHFTextArea';
import { TeethSelectRadioInput } from 'components/TeethSelectRadioInput/TeethSelectRadioInput';
import { TreatmentGoalsInput } from 'components/TreatmentGoalsInput/TreatmentGoalsInput';
import {
  TEETH_TO_EXTRACT_OPTIONS,
  TOOTH_MOVEMENT_RESTRICTIONS_OPTIONS
} from 'constants/TPVFormOptions';
import { translate } from 'utils';
import { useBracesTemplatizedOcclusionFields } from 'utils/customHooks/useBracesTemplatizedOcclusionFields';

type TreatmentSpecificationsExternalZenplusProps = {
  formType: string;
  isDisabled?: boolean;
  isReferral?: boolean;
  source?: string;
};
const TreatmentSpecificationsExternalZenplus: FC<
  TreatmentSpecificationsExternalZenplusProps
> = ({ formType, isDisabled, isReferral, source }) => {
  const applyBracesTemplatizedOcclusionFields =
    useBracesTemplatizedOcclusionFields();
  const isPrescriptionSummary = source === 'PrescriptionSummary';
  return (
    <div
      className={classNames('flex flex-col rounded-md gap-y-10', {
        'bg-GRAY5': !isReferral
      })}
    >
      <TreatmentGoalsInput
        disabled={isDisabled}
        applyDisableStyles={false}
        isZenplus={true}
        classname={classNames('flex', {
          'md:space-x-4': !isPrescriptionSummary,
          'md:flex-col': isPrescriptionSummary
        })}
        fieldName={`treatmentSpecifications.${formType}.treatmentGoals`}
        source={source}
      />
      <ArchesToTreatInput
        isNewTemplatizedPrescription={true}
        disabled={isDisabled}
        inputName={`treatmentSpecifications.${formType}.archesToTreat`}
        label={translate('2.Arches.to.treat')}
      />
      <TeethSelectRadioInput
        title={translate('3.Tooth.movement.restrictions')}
        radioGroupName={`treatmentSpecifications.${formType}.toothMovementRestrictions.toothMovementRestrictions`}
        radioGroupOptions={TOOTH_MOVEMENT_RESTRICTIONS_OPTIONS}
        teethSelectionName={`treatmentSpecifications.${formType}.toothMovementRestrictions.teethNotToMove`}
        disabled={isDisabled}
        animateLabel={`treatmentSpecifications.${formType}.toothMovementRestrictions.teethNotToMove`}
      />
      <OverbiteInput
        source={source}
        name={`treatmentSpecifications.${formType}.overbite`}
        disabled={isDisabled}
        applyDisableStyles={false}
        isNewTemplatizedPrescription={true}
      />
      <MidlinesInput
        disabled={isDisabled}
        name={`treatmentSpecifications.${formType}.midlines.midlines`}
        applyDisableStyles={false}
        isNewTemplatizedPrescription={true}
        fieldObject={{
          shiftUpper: `treatmentSpecifications.${formType}.midlines.shiftUpper`,
          shiftLower: `treatmentSpecifications.${formType}.midlines.shiftLower`,
          shiftLowerPosition: `treatmentSpecifications.${formType}.midlines.shiftLowerPosition`,
          shiftUpperPosition: `treatmentSpecifications.${formType}.midlines.shiftUpperPosition`
        }}
      />
      <TeethSelectRadioInput
        title={translate('6.Teeth.Extractions')}
        radioGroupName={`treatmentSpecifications.${formType}.extractTeeth.extractTeeth`}
        radioGroupOptions={TEETH_TO_EXTRACT_OPTIONS}
        teethSelectionName={`treatmentSpecifications.${formType}.extractTeeth.teethToExtract`}
        message={translate('teethExtraction.message.zenplus')}
        disabled={isDisabled}
        animateLabel={`treatmentSpecifications.${formType}.extractTeeth.teethToExtract`}
      />
      <ProsthesisRestorationInput
        isNewTemplatizedPrescription={true}
        title={translate('7.Prosthesis.Restorations')}
        disabled={isDisabled}
        fieldName={`treatmentSpecifications.${formType}.prosthesis`}
      />
      {applyBracesTemplatizedOcclusionFields || isPrescriptionSummary ? (
        <OcclusionCorrectionInput
          disabled={isDisabled}
          inputName={`treatmentSpecifications.${formType}.occlusionCorrection`}
          teethFieldName={`treatmentSpecifications.${formType}.occlusionCorrectionTeeth`}
          label={translate('8.Occlusion.Correction')}
          animateLabel={`treatmentSpecifications.${formType}.occlusionCorrectionTeeth`}
        />
      ) : null}
      <RHFTextArea
        inputProps={{
          placeholder: translate('settings.please_type_here'),
          disabled: isDisabled
        }}
        title={translate('9.Special Notes')}
        name={`treatmentSpecifications.${formType}.specialNotes`}
        autogrow
      />
    </div>
  );
};

export default TreatmentSpecificationsExternalZenplus;
